import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/context';
import { validateEmail } from '@/auth/utils';
import { useTranslation } from 'react-i18next';

const ForgotPasswordPage: React.FC = () => {
  const { forgotPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('auth');
  const { t: tErr } = useTranslation('errors');
  const returnTo = useMemo(() => {
    const raw = searchParams.get('returnTo');
    return raw && raw.startsWith('/') ? raw : '/login';
  }, [searchParams]);
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateEmail(email)) {
      setError(tErr('enterValidEmail'));
      return;
    }

    setIsSubmitting(true);
    try {
      await forgotPassword(email, { continuePath: returnTo });
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : tErr('passwordResetFailed');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-[#E2E8F0] bg-white p-10 shadow-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-black text-[#0F172A]">{t('resetPassword')}</h1>
          <p className="mt-2 text-sm text-[#64748B]">
            {t('enterEmailSendReset')}
          </p>
        </div>

        {submitted ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
              {t('resetLinkSent', { email })}
            </div>
            <p className="text-sm text-[#64748B]">
              {t('openLinkToSetPassword')}
            </p>
            <Link
              to={returnTo}
              className="block h-12 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 text-center text-sm font-black leading-[3rem] text-[#0F172A] transition-colors hover:border-[#E10613] hover:text-[#E10613]"
            >
              {t('returnToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-widest text-[#64748B]">{t('email')}</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="h-12 w-full rounded-lg border border-[#E2E8F0] bg-white px-4 text-[#0F172A] outline-none transition-colors placeholder:text-[#CBD5E1] focus:border-[#E10613]"
                placeholder="name@bank.com"
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="h-12 w-full rounded-lg bg-[#E10613] font-black text-white transition-colors hover:bg-[#C1121F] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? t('sendingResetLink') : t('sendResetLink')}
            </button>

            <Link
              to={returnTo}
              className="block text-center text-sm text-[#64748B] underline underline-offset-4 hover:text-[#0F172A]"
            >
              {t('backToLogin')}
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
